import { Alert, Linking } from "react-native";
import * as Sentry from "@sentry/react-native";
import * as Notifications from "expo-notifications";
import Constants from "expo-constants";
import { useCallback, useEffect } from "react";
import analytics from "@react-native-firebase/analytics";
import { useRouter } from "expo-router";

import IdentityService from "../services/user/identity";

import { type AppUserSession } from "@/schemas/auth/session";
import {
  type AppNotificationImageLikeData,
  AppNotificationImageLikeDataSchema,
  AppNotificationSchema,
  AppNotificationType,
} from "@/schemas/notification/app_notification_types";
import { LoggingService } from "@/services/internal/logging";
import MediaService from "@/services/user/media";
import { prepareImageDetailsNavigation } from "@/utils/router";

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowAlert: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

export async function getPushNotificationToken(
  showAlert: boolean,
): Promise<string | null> {
  const { status: existingStatus, canAskAgain } =
    await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== "granted" && canAskAgain) {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== "granted") {
    await analytics().logEvent("app_notification_disabled");
    // Do not show an alert if the user has denied permission and can't be asked again
    if (canAskAgain && showAlert) {
      Alert.alert(
        "Push Notification Needed",
        "Push notification is needed to improve your experience. You can enable it in your device settings.",
        [
          { style: "cancel", text: "Not Now" },
          {
            onPress: () => Linking.openSettings(),
            text: "Open Settings",
          },
        ],
      );
    }
    return null;
  }

  try {
    const projectId = Constants.expoConfig?.extra?.["eas"].projectId;
    const token = await Notifications.getExpoPushTokenAsync({
      projectId,
    });
    await analytics().logEvent("app_notification_enabled");
    return token?.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}

export async function setupNotificationAfterSigningIn(
  session: AppUserSession,
): Promise<string | null> {
  const token = await getPushNotificationToken(false);
  try {
    if (token !== null && token !== session.identity.pushToken) {
      const identity = new IdentityService(session);
      await identity.registerPushToken(token);
    }
    return token;
  } catch (error) {
    Sentry.captureException(error);
    // Do not alert the user. When expo service is down, the user will be spammed with alerts.
    return null;
  }
}

export async function setupNotificationBeforeSigningIn(): Promise<void> {
  await getPushNotificationToken(true);
}

export function useNotificationRedirectHandler(
  session: AppUserSession | null,
): void {
  const router = useRouter();
  const lastNotificationResponse = Notifications.useLastNotificationResponse(); // for notifications tap that launched the app while the app is not running

  const processNotificationResponse = useCallback(
    async (response: Notifications.NotificationResponse): Promise<void> => {
      // Process payload
      const payload = response?.notification.request.content.data;
      const notificationContent = response?.notification.request.content;

      const isChatNotification =
        notificationContent?.body === "You received a new message. 😉" &&
        notificationContent?.title === "Kai Social";

      let validatedPayload;
      try {
        validatedPayload = AppNotificationSchema.parse(payload);
      } catch (error) {
        if (isChatNotification) {
          // Todo; update chat notification payload to be something compatible with AppNotificationSchema.
          // Sentry Issue: https://dow-labs.sentry.io/issues/6465189154/?project=4506900449001472&query=is%3Aunresolved&referrer=issue-stream&stream_index=1
          return;
        }
        Sentry.captureException(error, {
          extra: {
            message: "Payload validation error",
            content: response?.notification.request.content,
            data: payload,
          },
        });

        return;
      }

      // Log notification open
      const notificationLogger = new LoggingService();
      await notificationLogger.logNotificationOpen({
        notificationId: validatedPayload.id,
      });

      function redirectToUserNotificationsScreen(): void {
        router.push("/(main)/shared/user-notifications");
      }

      // IMAGE_LIKE redirect function
      async function redirectToImageDetailsScreen(
        payloadData: AppNotificationImageLikeData,
      ): Promise<void> {
        if (!session) {
          return;
        }
        const mediaService = new MediaService(session);
        let image;

        try {
          image = await mediaService.getImageMediaByImageId(
            payloadData.imageId,
          );
          const { navigationParams } = prepareImageDetailsNavigation(image);
          router.push(navigationParams);
        } catch (error) {
          Alert.alert("Oops", "This image is no longer available.");
          // Sentry.captureException(error);
        }
      }

      // Calling corresponding redirection function based on notification type
      switch (validatedPayload.type) {
        case AppNotificationType.AUTH_WELCOME: {
          return;
        }
        case AppNotificationType.IMAGE_LIKE: {
          const validatedPayloadData = AppNotificationImageLikeDataSchema.parse(
            validatedPayload.data,
          );
          redirectToImageDetailsScreen(validatedPayloadData);
          break;
        }
        case AppNotificationType.WINK: {
          redirectToUserNotificationsScreen();
          break;
        }
        case AppNotificationType.NEW_FOLLOWER: {
          redirectToUserNotificationsScreen();
          break;
        }
        case AppNotificationType.FOLLOWING_IMAGE_UPLOAD: {
          redirectToUserNotificationsScreen();
          break;
        }
        default: {
          Sentry.captureException(new Error("Unsupported notification type"), {
            extra: {
              type: validatedPayload.type,
              rawPayload: validatedPayload,
            },
          });
        }
      }
    },
    [router, session],
  );

  // Handle the most recent notification tap
  // https://docs.expo.dev/push-notifications/what-you-need-to-know/#push-notification-behaviors
  // whenever NotificationResponseReceivedListener is triggered,
  // the same would apply to the useLastNotificationResponse hook.
  // Hence we don't need to use NotificationResponseReceivedListener?
  useEffect(() => {
    if (lastNotificationResponse) {
      processNotificationResponse(lastNotificationResponse);
      void Notifications.clearLastNotificationResponseAsync();
    }
  }, [lastNotificationResponse, processNotificationResponse]);
}
